import React, { useEffect, useRef, useMemo } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { type FormikProps, withFormik } from "formik";
import { noop } from "lodash";

import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import LinkForm from "features/Library/ExternalLinks/Forms/LinkForm";
import { externalLinkValidationSchema } from "features/Library/ExternalLinks/Forms/ExternalLinkForm.schema";
import { resetState as resetExternalLinkEntityState } from "features/Library/ExternalLinks/state/ExternalLinkEntityStateSlice";
import {
  createExternalLinkThunk,
  updateExternalLinkThunk,
} from "features/Library/ExternalLinks/state/thunks/externalLinkCommandThunk";
import { withAutosave, type AutosaveProps } from "utils/withAutosave";
import Restricted from "features/Application/Restricted";
import { RolePermissions } from "enums";
import { type WizardFormBase } from "interfaces";
import { type ExternalLinkFormData } from "../types/externalLinkTypes";
import { saveLink } from "../state/ExternalLinkDetailsSlice";
import { useEventCallback } from "hooks/useEventCallback";

interface ConfigureOwnProps extends WizardFormBase {
  onIsValidChange?: (isValid: boolean) => void;
  subscribeOnDiscarded?: (subscriber: () => void) => void;
  unSubscribeOnDiscarded?: (subscriber: () => void) => void;
}

// Props from Redux
const mapStateToProps = (state: RootState) => {
  const base = state.library.externalLinks;
  return {
    id: base.entityState.entityId,
    isCreating: base.entityState.changingEntityState,
    isDraft: true,
    currentLinkData: base.details.link,
    isFetching: base.details.isLoading,
    isLoaded: base.details.isLoaded,
    isLoading: base.details.isLoading,
    isPurchased: base.details.link?.isPurchased,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  createLink: (payload: ExternalLinkFormData) => dispatch(createExternalLinkThunk(payload)),
  updateLink: (payload: ExternalLinkFormData) => dispatch(updateExternalLinkThunk(payload)),
  saveLink: (payload: ExternalLinkFormData) => dispatch(saveLink(payload)),
  resetState: () => dispatch(resetExternalLinkEntityState()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

// All props combined for the Configure component
type ConfigurePropsAll = ConfigureOwnProps & FormikProps<ExternalLinkFormData> & PropsFromRedux & AutosaveProps;

// Helper to map form data to payload (e.g., trimming)
const mapToExternalLinkPayload = (values: ExternalLinkFormData): ExternalLinkFormData => ({
  title: values.title.trim(),
  description: values.description.trim(),
  url: values.url.trim(),
  urlText: values.urlText.trim(),
});

const ConfigureBase: React.FC<ConfigurePropsAll> = (props) => {
  const { resetState, createLink, updateLink, values, isValid, onIsValidChange, saveLink } = props;
  const isEntityCreated = useMemo(() => props.id > 0 || props.isCreating, [props.id, props.isCreating]);

  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    onIsValidChange?.(isValid);
  }, [isValid, onIsValidChange]);

  const onNext = useEventCallback(() => {
    saveLink(values);
    if (!isEntityCreated) {
      createLink(mapToExternalLinkPayload(valuesRef.current));
    } else {
      updateLink({ ...mapToExternalLinkPayload(valuesRef.current), id: props.id });
    }
  }, [createLink, isEntityCreated, props.id, saveLink, updateLink, values]);

  const onPrevious = useEventCallback(() => {
    if (!isEntityCreated) return;
    updateLink({ ...mapToExternalLinkPayload(valuesRef.current), id: props.id });
  }, [isEntityCreated, props.id, updateLink]);

  // Wizard step handlers
  useEffect(() => {
    props.acceptHandlers?.({
      onNext,
      onPrevious,
      onClose: resetState,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Restricted
      permissions={[RolePermissions.AssetsCreate]}
      renderContent={(hasPermission) => (
        <LinkForm
          {...props}
          disabled={props.disabled || !hasPermission}
          disablePreventTransitionPrompt={isEntityCreated}
          isReadOnlyView={props.isPurchased}
        />
      )}
    />
  );
};

// Applying HOCs
const withAutosaveComponent = withAutosave<
  ConfigurePropsAll,
  ExternalLinkFormData,
  ExternalLinkFormData & { id?: number }
>({
  getInitValues: (props) =>
    mapToExternalLinkPayload(props.currentLinkData || { title: "", description: "", url: "", urlText: "" }),
  stateProvider: mapToExternalLinkPayload,
  entityUpdater: (props) => props.updateLink,
  isDeffered: (props) => !props.isLoaded,
})(ConfigureBase);

const component = withFormik<PropsFromRedux & ConfigureOwnProps, ExternalLinkFormData>({
  mapPropsToValues: (props) =>
    props.currentLinkData && props.id
      ? { ...props.currentLinkData, id: props.id }
      : {
          title: "",
          description: "",
          url: "",
          urlText: "",
          id: -1,
        },
  validationSchema: externalLinkValidationSchema,
  enableReinitialize: true, // Important for when currentLinkData loads from Redux
  handleSubmit: noop, // Autosave or explicit wizard next handles saving
})(withAutosaveComponent);
const ConnectedComponent = connector(component);
export default ConnectedComponent;
